import { useEffect, useRef, useState } from "react";
import { GlobalWorkerOptions, getDocument, type PDFDocumentProxy } from "pdfjs-dist";
import { logError } from "../lib/logger.js";

GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

const NEAR_BOTTOM_PX = 24;

interface Props {
  url: string;
  title: string;
  version: string;
  onReachedEnd: () => void;
  onClose: () => void;
}

export function HandbookViewerModal({ url, title, version, onReachedEnd, onClose }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const reachedEndRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let doc: PDFDocumentProxy | null = null;

    async function render() {
      setLoading(true); setError("");
      try {
        doc = await getDocument(url).promise;
        if (cancelled) return;
        const container = scrollRef.current;
        if (!container) return;
        container.innerHTML = "";

        for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
          if (cancelled) return;
          const page = await doc.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.4 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.display = "block";
          canvas.style.margin = "0 auto 12px";
          canvas.style.maxWidth = "100%";
          canvas.style.boxShadow = "0 1px 4px rgba(0,0,0,0.15)";
          const ctx = canvas.getContext("2d");
          if (!ctx) continue;
          await page.render({ canvasContext: ctx, viewport }).promise;
          if (cancelled) return;
          container.appendChild(canvas);
        }

        if (!cancelled) checkScrollPosition();
      } catch (err) {
        if (!cancelled) {
          logError("HandbookViewerModal.render", err, { url });
          setError("Could not load the handbook. Please try again.");
        }
      }
      if (!cancelled) setLoading(false);
    }

    void render();
    return () => {
      cancelled = true;
      void doc?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  function checkScrollPosition() {
    const el = scrollRef.current;
    if (!el || reachedEndRef.current) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - NEAR_BOTTOM_PX) {
      reachedEndRef.current = true;
      onReachedEnd();
    }
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)",
          width: "min(760px, 100%)", height: "min(90vh, 960px)",
          display: "flex", flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)", overflow: "hidden",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--color-surface-200)" }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{title} (v{version})</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "var(--color-surface-400)", lineHeight: 1 }}>×</button>
        </div>

        {error ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center", color: "var(--color-error-fg)", fontSize: 14 }}>
            {error}
          </div>
        ) : (
          <div style={{ flex: 1, position: "relative", minHeight: 0 }}>
            {loading && (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-surface-500)", fontSize: 13, background: "var(--color-surface-100)" }}>
                Loading handbook…
              </div>
            )}
            <div
              ref={scrollRef}
              onScroll={checkScrollPosition}
              style={{ height: "100%", overflowY: "auto", padding: 16, background: "var(--color-surface-100)" }}
            />
          </div>
        )}

        <div style={{ padding: "10px 20px", borderTop: "1px solid var(--color-surface-200)", fontSize: 12, color: "var(--color-surface-500)" }}>
          Scroll to the end of the document to enable acknowledgment.
        </div>
      </div>
    </div>
  );
}
